import { useCallback, useMemo } from "react";

import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { TST_DEFAULT_PAGE_SIZE } from "@impero-com/ui";
import {
    getCoreRowModel,
    useReactTable,
    getPaginationRowModel,
} from "@tanstack/react-table";
import Link from "antd/es/typography/Link";
import moment from "moment-timezone";
import { Control, ControllerRenderProps, useWatch } from "react-hook-form";

import {
    ControlWithoutFieldsResources,
    Month,
    UserGroupView,
} from "api/generated/model";
import { useControlsSuspenseQuery } from "org/control/queries/useControlsQuery";

import { useUserGroupsQuery } from "../../../../user/queries/useUserGroupsQuery";
import { useControlActivitiesEstimationQuery } from "../queries/useControlActivitiesEstimationQuery";
import { TestingControlFormSchema } from "../testingControlSchema";
import { formatBulkCreatedTestsToNumber } from "../utils";

import {
    bulkControlsTableColumnHelper,
    getBulkControlsEstimatedActivitiesColumn,
    getBulkControlsProgramColumn,
    getBulkControlsSchedulingColumn,
    getBulkControlsTitleColumn,
} from "./bulkUtils";
import { BULK_CONTROLS_TITLE_COLUMN_ID } from "./consts";

export function useBulkControlsTable(
    control: Control<TestingControlFormSchema>,
    field: ControllerRenderProps<TestingControlFormSchema, "selectedControlIds">
) {
    const { t } = useTranslation();
    const { data: controlsData } = useControlsSuspenseQuery();
    const samplingTagIds = useWatch({ control, name: "samplingTagIds" });
    const period = useWatch({ control, name: "period" });
    const dueDate = useWatch({ control, name: "dueDate" });

    // Convert dates to NaiveMonthYear format for the API
    const hasPeriod = Boolean(period?.start && period?.end);
    const periodStart = period?.start
        ? {
              month: moment(period.start).locale("en").format("MMMM") as Month,
              year: moment(period.start).year(),
          }
        : { month: "January" as Month, year: 2025 };

    const periodEnd = period?.end
        ? {
              month: moment(period.end).locale("en").format("MMMM") as Month,
              year: moment(period.end).year(),
          }
        : { month: "December" as Month, year: 2026 };

    const testingControlDueDate = moment(dueDate).format("YYYY-MM-DD");

    // Fetch estimated activities for selected controls
    const { data: estimationData } = useControlActivitiesEstimationQuery(
        {
            controlIds: field.value ?? [],
            periodStart,
            periodEnd,
            tagIds: samplingTagIds,
            testingControlDueDate,
        },
        hasPeriod
    );

    // Create a map of control ID to total count for efficient lookup
    const estimationMap = useMemo(() => {
        const map = new Map<string, number>();
        if (estimationData?.counts) {
            estimationData.counts.forEach((item) => {
                map.set(item.controlId, item.totalCount);
            });
        }
        return map;
    }, [estimationData]);

    const tableData = useMemo(
        () =>
            controlsData.content
                .map((listDto) => listDto.resource)
                .filter((control) => field.value?.includes(control.id)),
        [controlsData, field.value]
    );
    const handleRemove = useCallback(
        (id: string) => {
            field.onChange(field.value?.filter((value) => value !== id));
        },
        [field]
    );

    const { data: userGroupTagData } = useUserGroupsQuery();

    const columns = useMemo(
        () =>
            buildColumns(
                t,
                controlsData.linkedResources,
                handleRemove,
                samplingTagIds,
                userGroupTagData ?? [],
                estimationMap
            ),
        [
            t,
            controlsData.linkedResources,
            handleRemove,
            samplingTagIds,
            userGroupTagData,
            estimationMap,
        ]
    );

    const table = useReactTable({
        columns,
        data: tableData,
        initialState: {
            sorting: [
                {
                    id: BULK_CONTROLS_TITLE_COLUMN_ID,
                    desc: false,
                },
            ],
            pagination: {
                pageSize: TST_DEFAULT_PAGE_SIZE,
            },
        },
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    return {
        table,
    };
}

const TEST_COUNT_COLUMN_ID = "testCount";
const ACTIONS_COLUMN_ID = "actions";
function buildColumns(
    t: TFunction,
    resources: ControlWithoutFieldsResources,
    handleRemove: (id: string) => void,
    samplingTagIds: string[],
    userGroups: UserGroupView[],
    estimationMap: Map<string, number>
) {
    return [
        getBulkControlsTitleColumn(t),
        getBulkControlsProgramColumn(t, resources),
        getBulkControlsSchedulingColumn(t),
        bulkControlsTableColumnHelper.accessor(
            (row) =>
                formatBulkCreatedTestsToNumber(row, samplingTagIds, userGroups),
            {
                id: TEST_COUNT_COLUMN_ID,
                header: t("TestingControlView_Bulk_TestCountColumn"),
                cell: (info) => info.getValue(),
            }
        ),
        getBulkControlsEstimatedActivitiesColumn(t, estimationMap),
        bulkControlsTableColumnHelper.display({
            id: ACTIONS_COLUMN_ID,
            header: t("TestingControlView_Bulk_ActionsColumn"),
            cell: (info) => (
                <Link onClick={() => handleRemove(info.row.original.id)}>
                    {t("TestingControlView_Bulk_RemoveAction")}
                </Link>
            ),
        }),
    ];
}
